import { inject, Injectable } from '@angular/core';
import { FsessionService } from '../session/fsession.service';
import { Observable, Subject } from 'rxjs';

export type Permiso = {
  idSubmodulo: number,
  IdPermisoTipo: number,
}

export enum PermissionTypes {
  LECTURA = 1,
  ESCRITURA = 2,
  CREACION = 3,
  MODIFICACION = 4,
  ESPECIAL = 5,
  PRUEBA = 6,
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private _fseSession = inject(FsessionService);

  private activeRouteRolePermissions: Record<number, number[]> = {};
  private activeRouteId;
  private permissionsChanges = new Subject<number | null>();

  private activeRouteSpecialPermissions: Record<number, string[]> = {};

  private arraysEqual(a?: number[], b?: number[]) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  /**
   * Obtiene los módulos cargados actuales
   */
  getActiveSubmodules() {
    return Object.keys(this.activeRouteRolePermissions).map(Number);
  }

  /**
   * Obtiene el id del submódulo/ruta
   * @returns Id de la ruta actual
   */
  getActiveRouteId() {
    return this.activeRouteId;
  }
  
  /**
   * Guarda los permisos de una ruta en específico
   * @param permissions Permisos
   * @param idRoute Id de la Ruta
   */
  setActiveSubmodule(idRoute) {
    this.activeRouteId = idRoute;
  }
  
  /**
   * Guarda los permisos de una ruta en específico
   * @param permissions Permisos
   * @param idRoute Id de la Ruta
   */
  setRoutePermissions(permissions: number[], idRoute: number) {
    const prev = this.activeRouteRolePermissions[idRoute];
    this.activeRouteRolePermissions[idRoute] = permissions;

    if (!this.arraysEqual(prev, permissions)) {
      this.permissionsChanges.next(idRoute);
    }
  }

  /**
   * Guarda los permisos de una ruta en específico
   * @param permissions Permisos
   * @param idRoute Id de la Ruta
   */
  cleanRoutes() {
    if (Object.keys(this.activeRouteRolePermissions).length > 0) {
      this.activeRouteRolePermissions = {};
      this.permissionsChanges.next(null);
    }
  }

  /**
   * Obtiene los permisos para una ruta en específico
   * @param idRoute Id de la Ruta
   */
  getRoutePermissions(idRoute: number) {
    return this.activeRouteRolePermissions[idRoute];
  }

  /**
   * Obtiene los permisos para una ruta en específico
   * @returns
   */
  onPermissionsChange(): Observable<number | null> {
    return this.permissionsChanges.asObservable();
  }

  /**
   * Verifica si el rol actual tiene permiso para realizar una acción para un submódulo
   * @param permissionType Tipo de acción (Lectura, Escritura, Creación, Eliminación o Especial)
   * @param idSubmodulo Id del submódulo a verificar (Si es nulo se usa el submódulo actual)
   * @returns Booleano
   */
  hasPermissionTo(permissionType: PermissionTypes, idSubmodulo: number = null): boolean {
    const rolId = +this._fseSession.optenerValor('dato40');
    const submoduleId = idSubmodulo !== null ? idSubmodulo : this.activeRouteId;
    
    // Esto evaluará si el rol se debe tratar como administrador y dar permiso a todo
    // const isAdministrative = this._fseSession.optenerValor('dato49');
    const AdministrativeRoles = [9];
    const isAdministrative = AdministrativeRoles.includes(rolId);
    
    if (isAdministrative) return true;

    const permissions = this.activeRouteRolePermissions[submoduleId];

    if (!permissions) return false;

    return permissions.includes(permissionType);
  }

  /**
   * Guarda permisos especiales (key_identity) para una ruta/submódulo
   * @param specials arreglo de key_identity
   * @param idRoute id del submódulo
   */
  setRouteSpecialPermissions(specials: string[], idRoute: number) {
    const prev = this.activeRouteSpecialPermissions[idRoute];
    this.activeRouteSpecialPermissions[idRoute] = specials || [];

    // usar el mismo mecanismo de notificación si cambió
    if (!this.arraysEqual(prev as any, specials as any)) {
      this.permissionsChanges.next(idRoute);
    }
  }

  /**
   * Obtiene permisos especiales almacenados para un submódulo
   */
  getRouteSpecialPermissions(idRoute: number) {
    return this.activeRouteSpecialPermissions[idRoute] || [];
  }

  /**
   * Verifica si existe una llave especial para el submódulo actual / dado
   * @param specialKey key_identity (ej. 'sp_est')
   * @param idSubmodulo id del submódulo (opcional)
   */
  hasSpecialPermission(specialKey: string, idSubmodulo: number = null): boolean {
    if (!specialKey) return false;

    const submoduleId = idSubmodulo !== null ? idSubmodulo : this.activeRouteId;
    if (submoduleId == null) return false;

    const specials = this.activeRouteSpecialPermissions[submoduleId] || [];
    // El permiso especial SOLO se concede si la clave existe explícitamente
    return specials.includes(specialKey);
  }
}
